import { Directive, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { AbstractControl, FormArray, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms'
import { LoggerService } from "@bequeathed/ngx-app-core"
import { InterviewState, MessageService, Question } from "@bequeathed/ngx-services"
import { PageUtil } from "@bequeathed/ngx-utils"
import { QuestionUtils } from "../../../utils/question.utils"
import { BaseForm, ErrorInfo } from '../../base-form/base-form.component'

const LOG: LoggerService = LoggerService.get('BaseControl')

/**
 * Root of all custom form controls providing shared functionality
 *
 * @author Dan Bennett (dbennett)
 */
@Directive()
export abstract class BaseControl implements OnInit, OnDestroy {

    // Form details
    @Input() groupIndex: number | undefined
    // @ts-expect-error always input
    @Input() questionId: string
    // @ts-expect-error always input
    @Input() baseForm: BaseForm
    // @ts-expect-error always input
    @Input() form: UntypedFormGroup
    @Input() formName = 'interview'
    // @ts-expect-error always input
    @Input() question: Question
    // @ts-expect-error always input
    @Input() onEnter: (event: any) => void
    @Input() modelValue: any
    @Input() standalone = true
    @Input() editingResponse = false
    @Input() hasValidationError = false
    @Output() modelValueChange = new EventEmitter<any>()
    @Output() preventSave = new EventEmitter<boolean>()
    @Output() loading = new EventEmitter<boolean>()

    // @ts-expect-error always created
    control: AbstractControl
    customerQualifier: string | null = null

    messages = inject(MessageService)

    validators: ValidatorFn[] = []
    previousAnswer: string | string[] | undefined
    previousError: string | undefined

    ngOnInit(): void {
        LOG.debug('ngOnInit', 'Hello! I am your new BaseControl instance!')
        if (!this.control) {
            this.determineValidators()
            this.control = new UntypedFormControl(
                (typeof this.previousAnswer === 'string')
                    ? this.previousAnswer : this.previousAnswer?.length
                        ? this.previousAnswer[0] : null,
                this.validators
            )
        }
        this.form.controls[this.question.id!] = this.control
        if (this.standalone) {
            this.baseForm.questionControl = this
        } else {
            // Needed for grouped controls or when there are multiple controls on a single page
            this.baseForm.questionControls.push(this)
        }
        if (this.question.userResponse) {
            // Only apply the scalar userResponse here for single-value controls. FormArray-backed
            // controls (checkbox / charity) are multi-value: their userResponse is a '|'-delimited
            // string and the subclass has already pre-filled each child control from it in its own
            // ngOnInit (via `editingResponse`). Calling FormArray.setValue with that string throws
            // "value.forEach is not a function", so skip it and just flag that we're editing.
            if (!(this.control instanceof FormArray)) {
                this.control.setValue(this.question.userResponse)
            }
            this.editingResponse = true
        }
    }

    ngOnDestroy() {
        LOG.debug('ngOnDestroy', 'Goodbye! This BaseControl instance is being destroyed.')
        if (this.control) {
            this.control.setErrors(null)
            const myIndex = this.baseForm.questionControls.indexOf(this)!
            this.baseForm.questionControls.splice(myIndex, 1)
            const registeredId = this.question?.id
            if (registeredId && this.form?.controls?.[registeredId] === this.control) {
                this.form.removeControl(registeredId)
            }
        }
    }

    /**
     * Determines which validators to use by default
     */
    determineValidators(): void {
        if (this.question.required) {
            this.validators.push(Validators.required)
        }
    }

    controlHasError(): boolean {
        const controlErrors: ErrorInfo[] | undefined | null = this.baseForm.errorMap ? this.baseForm.errorMap.get(this.question.id!) : null
        // FixMe: this.control is considered invalid after touching the first control, despite it not having any errors
        const isInvalid: boolean =
            (this.control?.touched && (this.control.invalid || (!!controlErrors)))
            || (this.baseForm.isSubmitted() && !this.control?.touched)

        if (LOG.levelEnabled('TRACE') && isInvalid) {
            LOG.trace('controlHasError', `Question: [${this.question.id}] has error(s): ${JSON.stringify(controlErrors || this.control?.errors)}`)
        }

        if (isInvalid && this.control.errors) {
            this.baseForm.applyErrors(this.question.id!, this.control.errors)
        }
        return isInvalid
    }

    controlErrorClass(): string {
        if (this.hasValidationError) return 'fa-exclamation-circle'
        if (this.controlHasError()) return 'fa-times-circle'
        return 'fa-check-circle'
    }

    focus(): void {
        PageUtil.findNativeElement(this.question.id!)?.focus()
    }

    async validate(): Promise<ErrorInfo[] | undefined> {
        if (this.control?.disabled) {
            return []
        }

        this.control?.markAllAsTouched()
        this.control?.updateValueAndValidity()

        if (!this.control?.valid) {
            return this.convertToErrorInfo(this.control?.errors)
        }

        const qa: any = {}
        const cntrl = this.baseForm.questionControl
        qa[cntrl!.question.id!] = []

            const val = cntrl!.modelValue
            if (val) {
                if (typeof val === 'string') {
                    qa[cntrl!.question.id!].push(val)
                } else {
                    val.forEach((v: string) => qa[cntrl!.question.id!].push(v))
                }

        }
        return this.baseForm.errorMap.get(this.question.id!)
    }

    private convertToErrorInfo(errors: any): ErrorInfo[] {
        return Object
            .keys(errors || {})
            .filter((key: string) => errors[key])
            .map((key: string) => {
                if (LOG.levelEnabled('DEBUG')) {
                    LOG.debug('validate', `Control: [${this.question.id}] has errors: ${JSON.stringify(key)}`)
                }
                return {
                    field: this.question.id,
                    error: key
                } as ErrorInfo
            })
    }

    abstract applyValue(state: InterviewState | Question): void

    trim(): void {
        LOG.trace('trim', 'Not compatible')
    }

    /**
     * Determines an appropriate autocomplete value for the given control name.
     *
     * @param controlName the name of the control to parse an autocomplete value for
     */
    determineAutocompleteValue(controlName: string): string {

        if (controlName.indexOf('address.line') > -1) {
            return `address-line${controlName.charAt(controlName.indexOf('address.line') + 'AddressLine'.length)}`
        }

        if (controlName.indexOf('address.postcode') > -1) {
            return 'postal-code'
        }

        if (controlName.indexOf('address.city') > -1) {
            return 'address-level2'
        }

        if (controlName.indexOf('name') > -1) {
            return 'name'
        }

        if (controlName.indexOf('email') > -1) {
            return 'email'
        }
        return 'on'
    }

    determineErrorMessage(errorType: string, control: AbstractControl, question: Question): string | null {
        LOG.trace('determineErrorMessage', `Determining error message for ${errorType}`)

        return this.previousError = this.messages.getText(`error.${errorType}.${question.charityType}`)
            || this.messages.getText(`error.${errorType}.${question.id}`)
            || this.messages.getText(`error.${errorType}.${question.type}`)
            || this.messages.getText(`error.${errorType}.generic`)
    }

    onValueChange(event: any) {
        this.control.setValue(event.target.value)
        this.setModelValue(this.control.value)
    }

    setModelValue(newValue: any): void {
        LOG.debug('setModelValue', `New value for modelValue: ${newValue}`)
        this.modelValue = newValue
        this.modelValueChange.emit(this.modelValue)
    }

    text(
        optionalSuffix?: string | undefined | null,
    ): string | null {
        if (this.question) {
            return QuestionUtils.getText(this.baseForm.type, this.question, this.messages, optionalSuffix, false) || ''
        }
        return null
    }

    /**
     * Tries to resolve a message for the label element for the input fields
     */
    getLabel(): string {
        if (this.question) {
            return QuestionUtils.getPlainText(this.baseForm.type, this.question, this.messages, null, false) || ''
        }
        return ''
    }

    setPreviousResponse() {
        LOG.debug("setPreviousResponse", `Here is the previous user response for this question: ${this.question.userResponse}`)
        this.control.setValue(this.question.userResponse)
    }

    public displayValue(value: string, message?: string | undefined) {
        let answer = QuestionUtils.getAnswer(this.baseForm.type, this.question, this.messages, value)
        if (answer === value && message) {
            answer = this.messages.getText(message) || message
        }

        return answer || value
    }

    public optionCode(value: string): string {
        return QuestionUtils.getOptionDisplayValue(this.baseForm.type, this.question, this.messages, value)
    }
}
